import React from 'react'
import { store } from 'store'
import * as actionCreators from 'store/actions'

import { ShortcutsModal, ItemInputModal } from 'features'
import { sideContext, pageContext, moduleContext } from 'context'
import {
    FaLayerGroup,
    FaEdit,
    FaListUl, 
    FaColumns, 
    FaCompressAlt,
    FaKeyboard,
    FaExchangeAlt,
    FaBars,
    FaRulerVertical,
    FaCheck,
    FaFolderOpen,
    FaToggleOn,
    FaToggleOff,
} from 'react-icons/fa'
import * as cssTheme from 'theme'

export const commandContext = {
    SHORTCUTS: {
        title: 'Shortcuts',
        keywords: ['shortcut'],
        icon: <FaKeyboard/>,
        callback: () => { 
            const state = store.getState();
            if(state.content.modalID === 'shortcutsModal'){
                store.dispatch(actionCreators.setModal(null, null))
            } else {
                store.dispatch(actionCreators.setModal(<ShortcutsModal/>, 'shortcutsModal'))
            }
        }
    },
    ACCESS: {
        title: 'Access modules',
        keywords: ['access', 'module'],
        icon: <FaLayerGroup/>,
        callback: () => { 
            const state = store.getState();
            if(state.auth.activePageType !== pageContext.TYPES.PAGES) { return }

            store.dispatch(actionCreators.setAccess(!state.content.showAccess, state.content.access || 'CALENDAR', state.content.accessProps))
            store.dispatch(actionCreators.setModal(null,null))
        }
    },
    MENU: {
        title: 'Toggle side menu',
        keywords: ['side', 'menu'],
        icon: <FaBars/>,
        callback: () => {
            const state = store.getState();
            const side = state.content.side
            store.dispatch(actionCreators.setSide(side === sideContext.TYPES.OPENED ? sideContext.TYPES.CLOSED : sideContext.TYPES.OPENED))
            store.dispatch(actionCreators.setModal(null,null))
        }
    },
    TOGGLE_VIEW: {
        title: 'Toggle page view',
        keywords: ['view', 'page'],
        icon: <FaExchangeAlt/>,
        callback: () => {
            const state = store.getState();
            const data = state.db.present.data && state.db.present.data.toJS()
            if(state.auth.activePageType !== pageContext.TYPES.PAGES) { return }

            let page = data.pages[state.auth.activePageID]
            const currentView = page['page_view']
            const updatedView = currentView === pageContext.VIEWS.LIST_VIEW ? pageContext.VIEWS.KANBAN_VIEW : pageContext.VIEWS.LIST_VIEW
            page['page_view'] = updatedView
            store.dispatch(actionCreators.updatePage(page, data, state.auth.uid, state.content.sessionId))
            store.dispatch(actionCreators.setModal(null,null))
        }
    },
    LIST_VIEW: {
        title: 'List view',
        keywords: ['list'],
        icon: <FaListUl/>,
        callback: () => {
            const state = store.getState();
            const data = state.db.present.data && state.db.present.data.toJS()
            if(state.auth.activePageType !== pageContext.TYPES.PAGES) { return }

            let page = data.pages[state.auth.activePageID]
            page['page_view'] = pageContext.VIEWS.LIST_VIEW
            store.dispatch(actionCreators.updatePage(page, data, state.auth.uid, state.content.sessionId))
            store.dispatch(actionCreators.setModal(null,null))
        }
    },
    KANBAN_VIEW: {
        title: 'Kanban view',
        keywords: ['kanban'],
        icon: <FaColumns/>,
        callback: () => {
            const state = store.getState();
            const data = state.db.present.data && state.db.present.data.toJS()
            if(state.auth.activePageType !== pageContext.TYPES.PAGES) { return }

            let page = data.pages[state.auth.activePageID]
            page['page_view'] = pageContext.VIEWS.KANBAN_VIEW 
            store.dispatch(actionCreators.updatePage(page, data, state.auth.uid, state.content.sessionId))
            store.dispatch(actionCreators.setModal(null,null))
        }
    },
    WIDTH: {
        title: 'Toggle full width',
        keywords: ['width'],
        icon: <FaCompressAlt/>,
        callback: () => {
            const state = store.getState();
            if(state.auth.activePageType !== pageContext.TYPES.PAGES) { return }

            let updatedUser = {...state.auth.user}
            updatedUser['full_width'] = !updatedUser['full_width']
            store.dispatch(actionCreators.updateUser(updatedUser))
            store.dispatch(actionCreators.setModal(null,null))
        }
    },
    DIVIDER: {
        title: 'Toggle divider',
        keywords: ['divider'],
        icon: <FaRulerVertical/>,
        callback: () => {
            const state = store.getState();
            if(state.auth.activePageType !== pageContext.TYPES.PAGES) { return }

            let updatedUser = {...state.auth.user}
            updatedUser['item_divider'] = !updatedUser['item_divider']
            store.dispatch(actionCreators.updateUser(updatedUser))
            store.dispatch(actionCreators.setModal(null,null))
        }
    },
    NEW_ITEM: {
        title: 'Create item',
        keywords: ['item', 'create'],
        icon: <FaEdit/>,
        callback: () => {
            const state = store.getState();
            if(state.auth.activePageType !== pageContext.TYPES.PAGES) { return }

            if(state.content.modalID === 'itemInputModal'){
                store.dispatch(actionCreators.setModal(null, null))
            } else {
                store.dispatch(actionCreators.setModal(<ItemInputModal/>, 'itemInputModal'))
            }
        }
    },
    COMPLETED: {
        title: 'Show completed items',
        keywords: ['item', 'complete'],
        icon: <FaCheck/>,
        callback: () => {
            const state = store.getState();
            if(state.auth.activePageType !== pageContext.TYPES.PAGES) { return }

            let updatedUser = {...state.auth.user}
            updatedUser['show_completed'] = !updatedUser['show_completed']
            store.dispatch(actionCreators.updateUser(updatedUser))
            store.dispatch(actionCreators.setModal(null,null))
        }
    },
    ARCHIVED_SECTIONS: {
        title: 'Show archived sections',
        keywords: ['section', 'archive'],
        icon: <FaFolderOpen/>,
        callback: () => {
            const state = store.getState();
            if(state.auth.activePageType !== pageContext.TYPES.PAGES) { return }

            let updatedUser = {...state.auth.user}
            updatedUser['show_archived'] = !updatedUser['show_archived']
            store.dispatch(actionCreators.updateUser(updatedUser))
            store.dispatch(actionCreators.setModal(null,null))
        }
    },
    DARK_THEME: {
        title: 'Dark theme',
        keywords: ['dark', 'theme'],
        icon: <FaToggleOn/>,
        callback: () => {
            const state = store.getState();
            if(state.auth.activePageType !== pageContext.TYPES.PAGES) { return }

            let updatedUser = {...state.auth.user}
            updatedUser['theme'] = 'Dark'
            store.dispatch(actionCreators.updateUser(updatedUser))
            store.dispatch(actionCreators.setModal(null,null))

            for (const [key, value] of Object.entries(cssTheme.dark)) {
                document.documentElement.style.setProperty(key,value);
            }
        
            for (const [key, value] of Object.entries(cssTheme.base)) {
                document.documentElement.style.setProperty(key,value);
            }
        }
    },
    LIGHT_THEME: {
        title: 'Light theme',
        keywords: ['light', 'theme'],
        icon: <FaToggleOff/>,
        callback: () => {
            const state = store.getState();
            if(state.auth.activePageType !== pageContext.TYPES.PAGES) { return }

            let updatedUser = {...state.auth.user}
            updatedUser['theme'] = 'Light'
            store.dispatch(actionCreators.updateUser(updatedUser))
            store.dispatch(actionCreators.setModal(null,null))

            for (const [key, value] of Object.entries(cssTheme.light)) {
                document.documentElement.style.setProperty(key,value);
            }
        
            for (const [key, value] of Object.entries(cssTheme.base)) {
                document.documentElement.style.setProperty(key,value);
            }
        }
    },
}